import { authenticationViews, Login } from "@rl-appliances/component-library";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { NotificationsLevel } from "../../lib/enums";
import { show } from "../../notifications/notifications";
import { PAGE_PATH } from "../../path";
import { ErrorResponse } from "../../requests/@types";
import { route, settings } from "../../shim/jsbridge";
import { legacyNavigateTo } from "../../utils";
import {
  ILoginCredentials,
  ILoginError,
  ILoginOptions,
  ILoginSubmit
} from "./@types";
import LoginLogo from "./LoginLogo";
import api from "./api";

const { product_name: appDescription, EULA_LINK } = settings || {};
const { userName } = window.tcbase;

export const loginBottomLinks = [
  {
    title: "ReversingLabs",
    link: "https://www.reversinglabs.com"
  },
  {
    title: "Browser Extension",
    link: "https://docs.reversinglabs.com/Integrations/Browser%20Extension/overview/#installation-and-setup"
  },
  {
    title: "Cookie Policy",
    link: "/cookie_policy"
  },
  {
    title: "Privacy Policy",
    link: "/privacy_policy"
  },
  {
    title: "End User License Agreement",
    link: EULA_LINK
  }
];

const LoginContainer = () => {
  const [loggingIn, setLoggingIn] = useState(false);
  const [initialView, setInitialView] = useState("");
  const [searchParams] = useSearchParams();
  const nextPage = searchParams.get("next") || PAGE_PATH.SEARCH;

  const { data: ssoLogin, isSuccess } = useQuery<
    AxiosResponse<ILoginOptions[]>,
    ErrorResponse<unknown>,
    ILoginOptions | undefined
  >({
    queryKey: ["login-options"],
    queryFn: () => api.getLoginOptions(),
    select: data => data?.data.find((option: ILoginOptions) => option.enabled)
  });

  const failed = searchParams.get("failed");
  const samlFailed = searchParams.get("saml2_failed");
  const groupsClaimFailed = searchParams.get("groups_claim_failed");
  const deniedGroupFailed = searchParams.get("denied_group_failed");
  const requiredGroupFailed = searchParams.get("required_group_failed");

  const getMessage = (): string => {
    if (failed || samlFailed) {
      return `Invalid login provided for the ${ssoLogin?.name} provider.`;
    }
    if (groupsClaimFailed) {
      return `Claim is not provided`;
    }
    if (deniedGroupFailed) {
      return `User belongs to the Deny group`;
    }
    if (requiredGroupFailed) {
      return `User does not belong to the Require group`;
    }
    return "";
  };

  const message = getMessage();

  useEffect(() => {
    if (isSuccess) {
      setInitialView(ssoLogin?.name || authenticationViews.USERPASS);
      if (message) {
        show({
          level: NotificationsLevel.ERROR,
          message
        });
      }
    }
  }, [isSuccess, ssoLogin?.name, message]);

  const baseLoginMutation = useMutation({
    mutationFn: (credentials: ILoginCredentials) => api.login(credentials),
    onSuccess: ({ data: { next } }: ILoginSubmit) => {
      legacyNavigateTo(next, false);
    },
    onError: ({ data: { errors }, status }: ILoginError) => {
      if (status === 403) {
        // 403 is returned when the csrf token is invalid
        legacyNavigateTo(nextPage, false);
        return;
      }
      show({
        level: NotificationsLevel.ERROR,
        message: errors || "Something went wrong. Please try again."
      });
      setLoggingIn(false);
    }
  });

  const onSubmit = (submittedValues: ILoginCredentials) => {
    if (submittedValues) {
      baseLoginMutation.mutate(submittedValues);
    }
    setLoggingIn(true);
  };

  useEffect(() => {
    if (userName) {
      legacyNavigateTo(nextPage, false);
    }
  }, [nextPage]);

  return (
    <Login
      logo={<LoginLogo appDescription={appDescription} />}
      appDescription={appDescription}
      onSubmit={onSubmit}
      loginView={initialView}
      nextPage={nextPage}
      ssoRoute={ssoLogin && route(ssoLogin?.route)}
      loading={loggingIn}
      next={nextPage}
      eulaLink={EULA_LINK}
      bottomLinks={loginBottomLinks}
    />
  );
};
export default LoginContainer;
